import { useId } from "react";
import BookingDialog from "./BookingDialog";
import QueryError from "./QueryError";
/**
 * Booking Details Modal
 * Hosts the booking history timeline, opened from BookingCard's "View Details" button.
 */

import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import type { Booking } from "@common/types";
import { getBookingHistory } from "../services/api";
import BookingTimeline from "./BookingTimeline";

interface BookingDetailsModalProps {
  booking: Booking;
  onClose: () => void;
}

export default function BookingDetailsModal({
  booking,
  onClose,
}: BookingDetailsModalProps) {
  const { t } = useTranslation();
  const titleId = useId();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["booking-history", booking.id, booking.date],
    queryFn: () => getBookingHistory(booking.id, booking.date),
  });

  return (
    <BookingDialog titleId={titleId} onClose={onClose}>
        <div className="flex justify-between items-start p-6 pb-4 border-b border-gray-100 sticky top-0 bg-white z-10 shrink-0">
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-sea-900">
              {t("bookingDetailsModal.title")}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5 font-mono">
              {booking.confirmationCode}
            </p>
          </div>
          <button
            onClick={onClose}
            className="min-h-11 min-w-11 -mr-2 text-primary-600 hover:text-sea-900 text-xl leading-none"
            aria-label={t("bookingDetailsModal.close")}
          >
            ×
          </button>
        </div>

        <div className="min-h-0 p-4 sm:p-6 pt-4 overflow-y-auto [overflow-wrap:anywhere]">
          {isLoading && (
            <div role="status" className="flex items-center justify-center py-8">
              <span className="sr-only">{t("partnerPages.loading")}</span>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sea-600" />
            </div>
          )}

          {isError && (
            <QueryError onRetry={() => void refetch()} />
          )}

          {data?.data && <BookingTimeline events={data.data} />}
        </div>
    </BookingDialog>
  );
}
